import React, { useState } from 'react'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import { TradingEntityMetaDetail, User, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { DialogField } from 'view/components/inputs'
import { ModalWindow, ModalFooter, ModalContent } from 'view/components/panels'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { UserSelectboxField } from 'view/shared/entity/UserSelectbox'
import { getReferenceDataEntitiesForSalesCoverAmend } from './utils'

interface SalesCoverageDialogProps extends ModalConsumerProps {
  tradingEntityMetaDetails: TradingEntityMetaDetail[]
  onUpdateBulk: typeof WorkflowActions.createEntitiesCreateAndSubmitReferenceData
  onClose?: () => unknown
}

const mapDispatchToProps = {
  onUpdateBulk: WorkflowActions.createEntitiesCreateAndSubmitReferenceData
}

const Content = styled(ModalContent)`
  flex-direction: row;
`

export const SalesCoverageDialogComponent: React.FunctionComponent<SalesCoverageDialogProps> = props => {
  const { tradingEntityMetaDetails, onUpdateBulk, onClose, showDialog } = props

  const [primaryCover, setPrimaryCover] = useState<string | null>(null)
  const [secondaryCover, setSecondaryCover] = useState<string | null>(null)

  const handleCancel = () => {
    onClose?.()
  }

  const handleApply = async () => {
    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_BULK_APPLY_SALES_COVER,
      message: Translation.SC_BULK_APPLY_SALES_COVER_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await showDialog(dialogArgs)

    if (dialogResult !== Translation.TC_BUTTON_YES) {
      onClose?.()
      return
    }

    const [referenceDataEntities, entitiesToDelete] = getReferenceDataEntitiesForSalesCoverAmend(
      tradingEntityMetaDetails,
      primaryCover,
      secondaryCover
    )
    if (referenceDataEntities.length > 0 || entitiesToDelete.length > 0) {
      onUpdateBulk(referenceDataEntities, entitiesToDelete)
    }
    onClose?.()
  }

  return (
    <ModalWindow title={Locale.translate(Translation.TC_BULK_APPLY_SALES_COVER)}>
      <Content>
        <DialogField>
          <UserSelectboxField
            label={Locale.translate(Translation.TC_PRIMARY_COVER)}
            getOptionText={(user: User) => user.username}
            value={primaryCover}
            onUpdate={(value: string | null) => setPrimaryCover(value)}
          />
        </DialogField>
        <DialogField>
          <UserSelectboxField
            label={Locale.translate(Translation.TC_SECONDARY_COVER)}
            getOptionText={(user: User) => user.username}
            value={secondaryCover}
            onUpdate={(value: string | null) => setSecondaryCover(value)}
          />
        </DialogField>
      </Content>
      <ModalFooter>
        <TextButton type={TextButtonType.Tertiary} onClick={handleCancel}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton type={TextButtonType.Primary} onClick={handleApply}>
          {Locale.translate(Translation.TC_BUTTON_APPLY)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const SalesCoverageDialog = withModal(connect(null, mapDispatchToProps)(SalesCoverageDialogComponent))
